import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, RefreshCw, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AdminChrome } from '../components/admin/AdminChrome';
import { SummaryCards, Toolbar } from '../components/admin/DashboardBits';
import { SubmissionsList } from '../components/admin/SubmissionsList';
import { SubmissionDrawer } from '../components/admin/SubmissionDrawer';
import { adminApi, useNoIndex } from '../components/admin/adminApi';

const blankFilters = { search: '', status: '', industry: '', from: '', to: '', sort: 'newest' };

export default function AdminDiscovery() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(blankFilters);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);
  useNoIndex();

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(filters.search), 320); return () => clearTimeout(t); }, [filters.search]);

  const query = useMemo(() => new URLSearchParams({
    search: debouncedSearch, status: filters.status, industry: filters.industry,
    from: filters.from, to: filters.to, sort: filters.sort,
  }).toString(), [debouncedSearch, filters.status, filters.industry, filters.from, filters.to, filters.sort]);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const result = await adminApi.list(`${query}&page=${page}`);
      if (result.unauthorized) { navigate('/admin/login', { replace: true }); return; }
      setData(result);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [query, page, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [query]);

  const updateFilters = patch => setFilters(prev => ({ ...prev, ...patch }));

  const changeStatus = async (entry, status) => {
    try {
      const updated = await adminApi.update(entry.id, { status });
      setData(prev => ({ ...prev, items: prev.items.map(i => (i.id === entry.id ? updated : i)) }));
      toast.success(`Status set to ${status}`);
      load(false);
    } catch (e) { toast.error(e.message); }
  };

  const saveNotes = useCallback(async (entry, notes) => {
    try {
      const updated = await adminApi.update(entry.id, { notes });
      setData(prev => ({ ...prev, items: prev.items.map(i => (i.id === entry.id ? updated : i)) }));
      toast.success('Notes saved');
      return true;
    } catch (e) { toast.error(e.message); return false; }
  }, []);

  const removeEntry = async entry => {
    try {
      await adminApi.remove(entry.id);
      setOpenId(null);
      toast.success('Submission deleted');
      load(false);
    } catch (e) { toast.error(e.message); }
  };

  const exportCsv = async scope => {
    setExporting(true);
    try { await adminApi.exportCsv(query, scope); toast.success(scope === 'all' ? 'Exported all submissions' : 'Exported current view'); }
    catch (e) { toast.error(e.message); } finally { setExporting(false); }
  };

  const signOut = async () => { await adminApi.logout(); navigate('/admin/login', { replace: true }); };

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize || 25;
  const pages = Math.max(Math.ceil(total / pageSize), 1);
  const selected = items.find(i => i.id === openId) || null;
  const filtered = Boolean(debouncedSearch || filters.status || filters.industry || filters.from || filters.to);

  return <AdminChrome title="Intake Form Submissions" subtitle={`${total} total${filtered ? ' in view' : ''}`} testId="admin-discovery-page"
    actions={<>
      <button type="button" className="admin-btn admin-btn-primary" onClick={() => exportCsv('filtered')} disabled={exporting} data-testid="admin-export-csv"><Download size={15} />Export CSV</button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button type="button" className="admin-btn admin-btn-ghost" aria-label="More actions" data-testid="admin-more-menu"><MoreHorizontal size={16} /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="status-menu">
          <DropdownMenuItem onClick={() => exportCsv('all')} data-testid="admin-export-all">Export all submissions</DropdownMenuItem>
          <DropdownMenuItem onClick={() => load()} data-testid="admin-refresh">Refresh list</DropdownMenuItem>
          <DropdownMenuItem onClick={signOut} data-testid="admin-menu-logout">Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>}>
    <>
      <SummaryCards stats={data?.stats} />
      <Toolbar filters={filters} industries={data?.industries || []} onChange={updateFilters} onClear={() => setFilters(blankFilters)} />
      <SubmissionsList items={items} loading={loading} filtered={filtered} openId={openId} onOpen={entry => setOpenId(entry.id)} onStatusChange={changeStatus} />

      {total > 0 && <div className="admin-pagination" data-testid="admin-pagination">
        <span data-testid="admin-page-info">Showing {items.length ? (page - 1) * pageSize + 1 : 0}–{(page - 1) * pageSize + items.length} of {total}</span>
        <div className="page-jump">
          <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page <= 1} data-testid="admin-prev-page">Previous</button>
          <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setPage(p => Math.min(p + 1, pages))} disabled={page >= pages} data-testid="admin-next-page">Next</button>
          <button type="button" className="admin-btn admin-btn-ghost" onClick={() => load()} aria-label="Refresh" data-testid="admin-refresh-button"><RefreshCw size={14} /></button>
        </div>
      </div>}

      <SubmissionDrawer entry={selected} sections={data?.sections || []} onClose={() => setOpenId(null)}
        onStatusChange={changeStatus} onSaveNotes={saveNotes} onDelete={removeEntry} />
    </>
  </AdminChrome>;
}
